/**
 * Persisted slide deck workspace (`/slides/$pageId`).
 *
 * Mirror of `SheetDocument` for presentations: binds the in-memory
 * `SlidesEditor` to local-first persistence (deck loaded from / autosaved to
 * the page's content block) with title editing and version history.
 */
import { createMemo, createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft } from "@fleet/icons";
import type { PageVersion } from "@folio/powersync";
import { restorePageVersionMutation, updatePageMutation } from "@folio/shared";
import { usePageDetailQuery } from "@folio/shared/solid";
import { useNavigate } from "@tanstack/solid-router";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { Button } from "@/components/ui/button";
import { VersionHistorySheet } from "@/features/notes/components/version-history-sheet";
import type { Presentation } from "../lib/office-io";
import { emptyPresentation } from "../lib/presentation-ops";
import { blockToPresentation, presentationToBlockNode } from "../lib/document-blocks";
import { useBlobDocument } from "../hooks/use-blob-document";
import { SlidesEditor } from "./slides-editor";
import { DocumentToolbarStatus } from "./document-toolbar-status";

const TITLE_SAVE_DEBOUNCE_MS = 400;

export function SlideDocument(props: { pageId: string }) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const navigate = useNavigate();

  const doc = useBlobDocument<Presentation>({
    pageId: () => props.pageId,
    fromBlock: blockToPresentation,
    toBlockNode: presentationToBlockNode,
  });

  const page = usePageDetailQuery({
    db: () => db(),
    drizzle,
    pageId: () => props.pageId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const resolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(page.data && page.data.deleted_at === null),
    isPending: () => page.isLoading,
  });

  const [draftTitle, setDraftTitle] = createSignal<string | null>(null);
  let titleTimeout: ReturnType<typeof setTimeout> | null = null;
  const title = createMemo(() => draftTitle() ?? page.data?.title ?? "");
  const handleTitleInput = (value: string) => {
    setDraftTitle(value);
    if (titleTimeout) {
      clearTimeout(titleTimeout);
    }
    titleTimeout = setTimeout(() => {
      titleTimeout = null;
      const dbInstance = drizzle();
      if (dbInstance) {
        // Mutation verifies write and THROWS if it does not land (see
        // `packages/shared/src/powersync/verified-write.ts`).
        void updatePageMutation(dbInstance, props.pageId, { title: value }).catch(
          (err: unknown) => {
            console.error("Error renaming slide deck:", err);
          },
        );
      }
      setDraftTitle(null);
    }, TITLE_SAVE_DEBOUNCE_MS);
  };

  const [versionsOpen, setVersionsOpen] = createSignal(false);
  const [restoring, setRestoring] = createSignal(false);
  const handleRestore = async (version: PageVersion) => {
    if (restoring()) {
      return;
    }
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    setRestoring(true);
    try {
      await restorePageVersionMutation(dbInstance, props.pageId, version);
      setVersionsOpen(false);
    } catch (err: unknown) {
      console.error("Error restoring slide deck version:", err);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <div class="flex h-full min-w-0 flex-1 items-center justify-center">
          <NotFoundState
            variant={resolution() === "offline" ? "offline" : "not-found"}
            title={resolution() === "offline" ? undefined : t(msg`Presentation not found`)}
            description={
              resolution() === "offline"
                ? undefined
                : t(msg`This presentation may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to presentations`),
              onClick: () => {
                void navigate({ to: "/slides" });
              },
            }}
          />
        </div>
      }
    >
      <div class="flex h-full min-h-0 min-w-0 flex-1 flex-col" data-testid="slide-document">
        <Show when={doc.isReady()} fallback={<div class="flex-1" />}>
          <SlidesEditor
            deck={() => doc.model() ?? emptyPresentation()}
            onDeckChange={doc.update}
            fileName={title}
            onFileNameChange={handleTitleInput}
            headerExtras={
              <div class="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  class="gap-1.5"
                  data-testid="slides-back-to-library"
                  aria-label={t(msg`Back to presentations`)}
                  onClick={() => {
                    doc.flush();
                    void navigate({ to: "/slides" });
                  }}
                >
                  <ArrowLeft class="h-4 w-4" />
                </Button>
                <DocumentToolbarStatus
                  isSaving={doc.isSaving}
                  saveError={doc.saveError}
                  lastSavedAt={doc.lastSavedAt}
                  onOpenVersions={() => setVersionsOpen(true)}
                />
              </div>
            }
          />
        </Show>
      </div>
      <VersionHistorySheet
        open={versionsOpen()}
        onOpenChange={setVersionsOpen}
        pageId={() => props.pageId}
        currentTitle={() => page.data?.title ?? ""}
        currentBlocks={() => doc.contentBlocks()}
        onRestore={(version) => {
          void handleRestore(version);
        }}
        restoring={restoring}
      />
    </Show>
  );
}
